import { createReducer, Action, on } from '@ngrx/store';
import * as _ from 'lodash';
import { DecorationsState, initialDecorationsState } from './decorators.state';
import * as actions from './decorators.actions';
import { initialLoadingState } from '../const';
import { initialState, readonlyContent } from './initial.state';

// #region Functions (1)

export const reducer = (
  state: DecorationsState = initialState,
  action: Action
) => decorationsReducer(state, action);

// ...existing code...

const updateNumberedReferencesHandler = (
  state,
  { references }
): DecorationsState => ({
  ...state,
  citations: {
    ...state.citations,
    references: [...references],
    unnumbered_references: state.citations.unnumbered_references,
    copiedNumberedCitationId: undefined,
    delFromDocCitationId: undefined,
    delFromDocCitationCount: undefined,
    deletedNumberedCitationId: null,
    deletedCitationFromRHP: undefined,
  },
});

// ...existing code...

// #endregion Functions (1)

// #region Variables (2)

const decorationsReducer = createReducer(
  initialDecorationsState,
  on(actions.setOrderedCitationIds, (state, { ids }) => ({
    ...state,
    citations: {
      ...state.citations,
      orderedReferenceIds: [...ids],
      copiedNumberedCitationId: undefined,
    },
  })),
  on(actions.setCurrentDocumentId, (state, { nodeId }) => ({
    ...state,
    nodeId,
  })),
  on(actions.loadingAllDecorators, (state, { nodeId }) => ({
    ...state,
    nodeId,
    initialLoadingState: {
      ...initialLoadingState,
      processing: true,
    },
  })),
  on(actions.loadingAllDecoratorsFailed, (state) => ({
    ...state,
    initialLoadingState: {
      ...initialLoadingState,
      failed: true,
    },
  })),
  on(
    actions.loadAllDecoratorsCompleted,
    (
      state,
      { citations, documentContent, trackChangeData, subTemplates, keywords }
    ) =>
      /** citationStack is empty on when user edits the document for the first time
       * On load of Edit mode then citationStack & unNumberedCitationStack will be updated with references and unnumberedreferences from API
       * On toggle of right hand panel then citationStack & unNumberedCitationStack will be updated with it's latest value
       */
      ({
        ...state,
        citationStack: [_.cloneDeep(citations.references)],
        unNumberedCitationStack: [_.cloneDeep(citations.unnumbered_references)],
        initialLoadingState: {
          ...initialLoadingState,
          completed: true,
        },
        citations: {
          ...state.citations,
          references: !state.isInfoDrawerToggled
            ? citations.references
            : state.citationStack[state.undoStackIndex],
          unnumbered_references: !state.isInfoDrawerToggled
            ? citations.unnumbered_references
            : state.unNumberedCitationStack[state.unNumRefUndoStackIndex],
          copiedNumberedCitationId: undefined,
        },
        deletedUnnumberedCitationId: null,
        deletedNumberedCitationId: null,
        documentContent,
        trackChangeData,
        citationTabIndex: state.citationTabIndex || 0,
        subTemplates,
        keywords,
        trackChangeRHPData: {
          suggestions: _.filter(
            trackChangeData.suggestions,
            (sug) => sug.isRHPSuggestion
          ),
          commentThreads: _.filter(
            trackChangeData.commentThreads,
            (cmnt) => cmnt.isRHPComment
          ),
        },
      })
  ),
  on(actions.updateCitations, (state, { citations }) => ({
    ...state,
    citations: {
      ...state.citations,
      ...citations,
      copiedNumberedCitationId: undefined,
      delFromDocCitationId: undefined,
      delFromDocCitationCount: undefined,
    },
  })),
  on(
    actions.setCitationsPrefix,
    (
      state,
      { referencePrefix, unnumberReferencePrefix, prefix, sufix, counterCase }
    ) => ({
      ...state,
      citations: {
        ...state.citations,
        referencePrefix,
        unnumberReferencePrefix,
        prefix,
        sufix,
        counterCase,
        copiedNumberedCitationId: undefined,
      },
    })
  ),
  on(actions.setCreatedReferenceId, (state, { referenceId }) => ({
    ...state,
    citations: {
      ...state.citations,
      createdReferenceId: referenceId,
    },
  })),
  on(
    actions.setEditorToggleTrackChange,
    (state, { rhpTrackChangesEnabled }) => ({
      ...state,
      citations: {
        ...state.citations,
        isTrackChangesEnabled: rhpTrackChangesEnabled,
      },
    })
  ),
  on(actions.updateUnnumberedReferences, (state, { references }) => {
    /** If user is inbetween two already undo'ed actions and creates a new insertion of unnumbered reference
     * Then, state.unNumberedCitationStack will be sliced from current state.unNumRefUndoStackIndex
     * Then, state.unNumberedCitationStack.unshift happens with the new unnumbered reference
     */
    if (state.unNumRefUndoStackIndex) {
      state.unNumberedCitationStack = _.cloneDeep(
        state.unNumberedCitationStack.slice(state.unNumRefUndoStackIndex)
      );
      state.unNumRefUndoStackIndex = 0;
    }
    /**
     * Avoiding a continous empty array added into Stack,
     * Scenario : On Discard All the multiple Insertion citation it'll remove from all,
     * but in stack it'll add one by one that will cause issue with undo fetch.
     */
    const isNotEmptyStack =
      references.length !== 0 || state.unNumberedCitationStack[0].length !== 0;

    const isDuplicateStack = _.isEqual(
      JSON.stringify(references),
      JSON.stringify(state.unNumberedCitationStack[0])
    );

    isNotEmptyStack &&
      !isDuplicateStack &&
      state.unNumberedCitationStack.unshift(_.cloneDeep(references));

    return {
      ...state,
      citations: {
        ...state.citations,
        references: state.citations.references,
        unnumbered_references: [...references],
        copiedNumberedCitationId: undefined,
      },
    };
  }),
  on(actions.updateNumberedReferences, updateNumberedReferencesHandler),
  on(
    actions.updateNumberedReferencesFromService,
    updateNumberedReferencesHandler
  ),
  on(actions.updateNumberedReferencesWithoutUndo, (state, { references }) => ({
    ...state,
    citations: {
      ...state.citations,
      references: [...references],
      unnumbered_references: state.citations.unnumbered_references,
      copiedNumberedCitationId: undefined,
      delFromDocCitationId: undefined,
      delFromDocCitationCount: undefined,
      deletedCitationFromRHP: undefined,
    },
  })),
  on(
    actions.updateUnNumberedReferencesWithoutUndo,
    (state, { references }) => ({
      ...state,
      citations: {
        ...state.citations,
        references: state.citations.references,
        unnumbered_references: [...references],
        copiedNumberedCitationId: undefined,
        delFromDocCitationId: undefined,
        delFromDocCitationCount: undefined,
        deletedCitationFromRHP: undefined,
      },
    })
  ),
  on(actions.setIsEditable, (state, { isEditable }) => ({
    ...state,
    isEditable,
  })),
  on(actions.setDeletedUnnumberedReferenceId, (state, { citationId }) => ({
    ...state,
    citations: {
      ...state.citations,
      deletedUnnumberedCitationId: citationId,
      copiedNumberedCitationId: undefined,
      deletedCitationFromRHP: undefined,
    },
  })),
  on(actions.setDeletedNumberedReferenceId, (state, { citationId }) => ({
    ...state,
    citations: {
      ...state.citations,
      deletedNumberedCitationId: citationId,
      copiedNumberedCitationId: undefined,
      deletedCitationFromRHP: undefined,
    },
  })),
  on(actions.setCitationUpdated, (state, { isCitationUpdated }) => ({
    ...state,
    citations: {
      ...state.citations,
      isCitationUpdated,
      copiedNumberedCitationId: undefined,
      deletedCitationFromRHP: undefined,
    },
  })),
  on(actions.setSelectedCitationId, (state, { citationId }) => ({
    ...state,
    citations: {
      ...state.citations,
      selectedNumberedCitationId: citationId,
      deletedCitationFromRHP: undefined,
    },
  })),
  on(actions.setReferenceEditorClicked, (state, { referenceEditor }) => ({
    ...state,
    referenceEditor,
  })),
  on(
    actions.setCopiedNumberedCitationId,
    (state, { copiedNumberedCitationId }) => ({
      ...state,
      citations: {
        ...state.citations,
        copiedNumberedCitationId,
        deletedCitationFromRHP: undefined,
      },
    })
  ),
  on(actions.setCopyEnable, (state, { isCopyEnable }) => ({
    ...state,
    isCopyEnable,
  })),
  on(
    actions.setSelectedReferenceId,
    (state, { id }): DecorationsState => ({
      ...state,
      citations: {
        ...state.citations,
        selectedReferenceId: id,
        deletedCitationFromRHP: undefined,
      },
    })
  ),
  on(actions.setUnNumUndoRef, (state, { references }) => ({
    ...state,
    citations: {
      ...state.citations,
      unnumbered_references: [...references],
      deletedCitationFromRHP: undefined,
    },
  })),
  on(actions.undoCitations, (state, { citations }) => ({
    ...state,
    citations: {
      ...state.citations,
      references: [...citations.references],
      deletedCitationFromRHP: undefined,
    },
  })),
  on(
    actions.setDeletedFromDocNumberedReferenceId,
    (state, { citationId, citationCount }) => ({
      ...state,
      citations: {
        ...state.citations,
        delFromDocCitationId: citationId,
        delFromDocCitationCount: citationCount,
        deletedCitationFromRHP: undefined,
      },
    })
  ),

  on(actions.setTrackchangeAndSuggestion, (state, { trackChangeData }) => ({
    ...state,
    trackChangeData,
  })),
  on(
    actions.onLoadCompleteReadonlyContent,
    (state, { trackChangeData, docContent, loaded, citations }) => ({
      ...state,
      readonlyContent: {
        trackChangeData,
        docContent,
        loaded,
        citations,
      },
    })
  ),
  on(
    actions.setSelectedChevronData,
    (state, { chevronData }): DecorationsState => ({
      ...state,
      citations: {
        ...state.citations,
        chevronData,
        deletedCitationFromRHP: undefined,
      },
    })
  ),
  on(actions.setInitialReadonlyStore, (state) => ({
    ...state,
    documentContent: '',
    citations: {
      ...state.citations,
      selectedReferenceId: undefined,
      chevronData: undefined,
      deletedCitationFromRHP: undefined,
    },
    citationStack: [],
    unNumberedCitationStack: [],
    unNumRefUndoStackIndex: 0,
    undoStackIndex: 0,
    readonlyContent,
    initialLoadingState: {
      ...initialLoadingState,
    },
    isInfoDrawerToggled: false,
  })),
  on(actions.setCitationTabIndex, (state, { index }) => ({
    ...state,
    citationTabIndex: index,
  })),
  on(actions.setIsInfoDrawerToggled, (state, { isInfoDrawerToggled }) => ({
    ...state,
    isInfoDrawerToggled,
  })),
  on(
    actions.setCiationsUndoStackIndex,
    (state, { unNumRefUndoStackIndex, undoStackIndex }) => ({
      ...state,
      undoStackIndex,
      unNumRefUndoStackIndex,
    })
  ),
  on(
    actions.setFragmentLinks,
    (state, { fragmentLinks }): DecorationsState => ({
      ...state,
      citations: {
        ...state.citations,
        fragmentLinks: {
          ...state.citations.fragmentLinks,
          incomingLinks: [...fragmentLinks.incomingLinks],
          outgoingLinks: [...fragmentLinks.outgoingLinks],
        },
      },
    })
  ),
  on(actions.setRHPTrackChangesData, (state, { trackChangeRHPData }) => ({
    ...state,
    trackChangeRHPData,
  })),
  on(actions.setDeletedCitationFromRHP, (state, { citationId }) => ({
    ...state,
    citations: {
      ...state.citations,
      deletedCitationFromRHP: citationId,
    },
  })),
  on(
    actions.setSelectedCommentsTabIndex,
    (state, { index }): DecorationsState => ({
      ...state,
      selectedCommentsTabIndex: index,
    })
  ),
  on(
    actions.updateCitationsInRHPWithTrackchangeData,
    (state, { citationStack, undoStackIndex }) => ({
      ...state,
      citationStack: [...citationStack],
      undoStackIndex,
    })
  ),
  on(
    actions.updateUnNumberedCitationsInRHPWithTrackchangeData,
    (state, { citationStack, unNumRefUndoStackIndex }) => ({
      ...state,
      unNumberedCitationStack: [...citationStack],
      unNumRefUndoStackIndex,
    })
  ),
  on(
    actions.updateUserPreferenceForTrackChangesView,
    (state, { userPreferenceForTrackChangesView }) => ({
      ...state,
      userPreferenceForTrackChangesView,
    })
  ),
  on(
    actions.loadDocumentAndContentComplete,
    (
      state,
      { citations, documentContent, trackChangeData, subTemplates, keywords }
    ) => ({
      ...state,
      initialLoadingState: {
        ...initialLoadingState,
        completed: true,
      },
      citations,
      documentContent,
      trackChangeData,
      subTemplates,
      keywords,
      readonlyContent: {
        trackChangeData,
        docContent: documentContent,
        loaded: true,
        citations,
      },
    })
  ),
  on(
    actions.setDocumentValidationStatus,
    (state, { documentValidationStatus }) => ({
      ...state,
      documentValidationStatus,
    })
  ),
  on(actions.resetDecoratorsState, () => ({ ...initialDecorationsState })),
  on(actions.setCMTitleValue, (state, { cmTitle }) => ({
    ...state,
    cmTitle,
  })),
  on(
    actions.setBaseHref,
    (state, { baseHrefForLinkPlugin, baseHrefPathUrl, baseHrefId }) => ({
      ...state,
      baseHrefForLinkPlugin,
      baseHrefPathUrl,
      baseHrefId,
    })
  )
);
// #endregion Variables (2)
